import React from 'react';
import { DashboardMetricsResponse } from '../../utils/api/Dashboard';
import { ReactComponent as FreshExamIcon } from '../../assets/icons/icPlusFreshExam.svg';
import { ReactComponent as PostExamIcon } from '../../assets/icons/icListPostExam.svg';
import { ReactComponent as ArmPrimerIcon } from '../../assets/icons/icLabArmPrimer.svg';
import { ReactComponent as CircleTrainingIcon } from '../../assets/icons/icCircleTraining.svg';
import { ReactComponent as MetricArrowIcon } from '../../assets/icons/icMetricArrow.svg';
import { ReactComponent as MetricBarsIcon } from '../../assets/icons/icMetricBars.svg';
import { ReactComponent as MetricCheckIcon } from '../../assets/icons/icMetricCheck.svg';
import { ReactComponent as MetricTeamIcon } from '../../assets/icons/icMetricTeam.svg';
import { ReactComponent as MetricArrowGreenIcon } from '../../assets/icons/icMetricArrowGreen.svg';
import { ReactComponent as MetricBarsGreenIcon } from '../../assets/icons/icMetricBarsGreen.svg';
import { ReactComponent as MetricCheckGreenIcon } from '../../assets/icons/icMetricCheckGreen.svg';
import { ReactComponent as MetricTeamGreenIcon } from '../../assets/icons/icMetricTeamGreen.svg';
import {
  DashboardCardPlaceholder,
  ArmScoreCard,
  TotalArmStrengthCard,
  MetricCardHeader,
  MetricTitle,
  MetricTitleGroup,
  MetricCardRangeLabel,
  MetricInfoIcon,
  MetricInfoTooltipWrap,
  MetricTooltipBubble,
  MetricSubGrid,
  MetricSubTile,
  MetricSubTileLabel,
  MetricSubTileValueRow,
  MetricSubTileNumber,
  MetricSubTileUnit,
  MetricSubTileSubtext,
  MetricTileArrowIconWrap,
  MetricTileBarsIconWrap,
  MetricTileCheckIconWrap,
  MetricTileTeamIconWrap,
  TestingList,
  TestingListItem,
  TestingLabel,
  TestingLabelIcon,
  TestingValue,
} from './sectionStyles';

const METRIC_HELP_COPY = {
  armScore:
    'Your ArmScore reflects the change in relative arm strength (arm strength divided by bodyweight) from your earliest to most recent exam within the selected date range.',
  totalArmStrength:
    'Your Arm Strength shows how much total strength your team has gained across all key movements (IR, ER, Scaption, and Grip) during the selected time period.',
  testing:
    'This shows the total number of ArmCare exams completed during the selected date range, including Fresh Exams, Post Exams, and Arm Primers.',
  training:
    'This reflects the total number of training sessions completed, including Strength, Hybrid, and Off Days.',
} as const;

const RANGE_LABELS: Record<string, string> = {
  all: 'All Time',
  '1W': 'Last Week',
  '1M': 'Last Month',
  '3M': 'Last 3 Months',
  '6M': 'Last 6 Months',
  '1Y': 'Last Year',
  custom: 'Custom Range',
};

function MetricHelpIcon({ label, text }: { label: string; text: string }) {
  return (
    <MetricInfoTooltipWrap>
      <MetricInfoIcon type="button" aria-label={label}>
        i
      </MetricInfoIcon>
      <MetricTooltipBubble role="tooltip">{text}</MetricTooltipBubble>
    </MetricInfoTooltipWrap>
  );
}

type TileConfig = {
  IconWrap: React.ElementType;
  icon: React.ReactNode;
  label: string;
  value: string;
  unit?: string;
  subtext?: string;
};

type MetricCardProps = {
  CardWrapper: React.ElementType;
  title: string;
  helpLabel: string;
  helpText: string;
  rangeLabel: string;
  variant: 'blue' | 'green';
  tiles: TileConfig[];
};

function MetricCard({ CardWrapper, title, helpLabel, helpText, rangeLabel, variant, tiles }: MetricCardProps) {
  return (
    <CardWrapper>
      <MetricCardHeader>
        <MetricTitleGroup>
          <MetricTitle>{title}</MetricTitle>
          {rangeLabel && <MetricCardRangeLabel>{rangeLabel}</MetricCardRangeLabel>}
        </MetricTitleGroup>
        <MetricHelpIcon label={helpLabel} text={helpText} />
      </MetricCardHeader>
      <MetricSubGrid $variant={variant}>
        {tiles.map((tile, i) => (
          <MetricSubTile key={i}>
            <tile.IconWrap>{tile.icon}</tile.IconWrap>
            <MetricSubTileLabel>{tile.label}</MetricSubTileLabel>
            <MetricSubTileValueRow>
              <MetricSubTileNumber>{tile.value}</MetricSubTileNumber>
              {tile.unit && <MetricSubTileUnit>{tile.unit}</MetricSubTileUnit>}
            </MetricSubTileValueRow>
            {tile.subtext && <MetricSubTileSubtext>{tile.subtext}</MetricSubTileSubtext>}
          </MetricSubTile>
        ))}
      </MetricSubGrid>
    </CardWrapper>
  );
}

type SummaryCardsProps = {
  metrics?: DashboardMetricsResponse;
  isLoading: boolean;
  isError: boolean;
  rangePreset?: string;
};

export const SummaryCards: React.FC<SummaryCardsProps> = ({
  metrics,
  isLoading,
  isError,
  rangePreset,
}) => {
  if (isError) {
    return <div>Unable to load dashboard metrics.</div>;
  }

  if (isLoading || !metrics) {
    return <div>Loading metrics...</div>;
  }

  const rangeLabel = rangePreset ? (RANGE_LABELS[rangePreset] ?? '') : '';

  const armScoreAvgGain = metrics.armScore?.avgGain ?? 0;
  const armScoreTotalGain = metrics.armScore?.totalGain ?? 0;
  const armScoreImproved = metrics.armScore?.improvedPercentage ?? 0;
  const armScoreTeamAvg = metrics.armScore?.teamAverage ?? 0;
  const armScorePlayerCount = metrics.armScore?.playerCount ?? 0;
  const armScoreImprovedCount = metrics.armScore?.improvedCount ?? 0;

  const strengthAvgGain = metrics.totalArmStrength?.avgGain ?? 0;
  const strengthTotalGain = metrics.totalArmStrength?.totalGain ?? 0;
  const strengthImproved = metrics.totalArmStrength?.improvedPercentage ?? 0;
  const strengthTeamAvg = metrics.totalArmStrength?.teamAverage ?? 0;
  const strengthPlayerCount = metrics.totalArmStrength?.playerCount ?? 0;
  const strengthImprovedCount = metrics.totalArmStrength?.improvedCount ?? 0;

  const testingTotal = metrics.testing?.totalExams ?? 0;
  const testingFresh = metrics.testing?.breakdown?.fresh ?? 0;
  const testingPost = metrics.testing?.breakdown?.post ?? 0;
  const testingArmPrimer = metrics.testing?.breakdown?.armPrimer ?? 0;

  const trainingTotal = metrics.training?.totalSessions ?? 0;
  const trainingStrength = metrics.training?.breakdown?.strength ?? 0;
  const trainingHybrid = metrics.training?.breakdown?.hybrid ?? 0;
  const trainingOffDay = metrics.training?.breakdown?.offDay ?? 0;

  const formatGain = (value: number) => {
    const sign = value > 0 ? '+' : value < 0 ? '−' : '';
    return `${sign}${Math.abs(Math.round(value))}`;
  };

  return (
    <>
      <MetricCard
        CardWrapper={ArmScoreCard}
        title="ARMSCORE METRICS"
        helpLabel="About ArmScore"
        helpText={METRIC_HELP_COPY.armScore}
        rangeLabel={rangeLabel}
        variant="blue"
        tiles={[
          {
            IconWrap: MetricTileArrowIconWrap,
            icon: <MetricArrowIcon />,
            label: 'Total Gain',
            value: armScoreTotalGain.toLocaleString(),
            unit: 'pts',
            subtext: `Across ${armScorePlayerCount} players`,
          },
          {
            IconWrap: MetricTileBarsIconWrap,
            icon: <MetricBarsIcon />,
            label: 'Avg Gain',
            value: formatGain(armScoreAvgGain),
          },
          {
            IconWrap: MetricTileCheckIconWrap,
            icon: <MetricCheckIcon />,
            label: 'Improved',
            value: `${armScoreImproved}%`,
            subtext: `${armScoreImprovedCount} players up`,
          },
          {
            IconWrap: MetricTileTeamIconWrap,
            icon: <MetricTeamIcon />,
            label: 'Team Avg',
            value: String(armScoreTeamAvg),
          },
        ]}
      />

      <MetricCard
        CardWrapper={TotalArmStrengthCard}
        title="ARM STRENGTH METRICS"
        helpLabel="About total arm strength"
        helpText={METRIC_HELP_COPY.totalArmStrength}
        rangeLabel={rangeLabel}
        variant="green"
        tiles={[
          {
            IconWrap: MetricTileArrowIconWrap,
            icon: <MetricArrowGreenIcon />,
            label: 'Total Gain',
            value: strengthTotalGain.toLocaleString(),
            unit: 'lbs',
            subtext: `Across ${strengthPlayerCount} players`,
          },
          {
            IconWrap: MetricTileBarsIconWrap,
            icon: <MetricBarsGreenIcon />,
            label: 'Avg Gain',
            value: formatGain(strengthAvgGain),
            unit: 'lbs',
          },
          {
            IconWrap: MetricTileCheckIconWrap,
            icon: <MetricCheckGreenIcon />,
            label: 'Improved',
            value: `${strengthImproved}%`,
            subtext: `${strengthImprovedCount} players up`,
          },
          {
            IconWrap: MetricTileTeamIconWrap,
            icon: <MetricTeamGreenIcon />,
            label: 'Team Avg',
            value: String(strengthTeamAvg),
            unit: 'lbs',
          },
        ]}
      />

      <DashboardCardPlaceholder>
        <MetricCardHeader>
          <MetricTitle>TESTING (TOTAL)</MetricTitle>
          <MetricHelpIcon label="About testing totals" text={METRIC_HELP_COPY.testing} />
        </MetricCardHeader>
        <p>
          <strong>{testingTotal.toLocaleString()}</strong> exams
        </p>
        <TestingList>
          <TestingListItem>
            <TestingLabel>
              <TestingLabelIcon bgColor="#DBEAFE" iconColor="#2563EB">
                <FreshExamIcon />
              </TestingLabelIcon>
              <span>Fresh Exams</span>
            </TestingLabel>
            <TestingValue>{testingFresh.toLocaleString()}</TestingValue>
          </TestingListItem>
          <TestingListItem>
            <TestingLabel>
              <TestingLabelIcon bgColor="#E0E7FF" iconColor="#4F46E5">
                <PostExamIcon />
              </TestingLabelIcon>
              <span>Post Exams</span>
            </TestingLabel>
            <TestingValue>{testingPost.toLocaleString()}</TestingValue>
          </TestingListItem>
          <TestingListItem>
            <TestingLabel>
              <TestingLabelIcon bgColor="#EBEDF2" iconColor="#475569">
                <ArmPrimerIcon />
              </TestingLabelIcon>
              <span>Arm Primer</span>
            </TestingLabel>
            <TestingValue>{testingArmPrimer.toLocaleString()}</TestingValue>
          </TestingListItem>
        </TestingList>
      </DashboardCardPlaceholder>

      <DashboardCardPlaceholder>
        <MetricCardHeader>
          <MetricTitle>TRAINING (TOTAL)</MetricTitle>
          <MetricHelpIcon label="About training totals" text={METRIC_HELP_COPY.training} />
        </MetricCardHeader>
        <p>
          <strong>{trainingTotal.toLocaleString()}</strong> sessions
        </p>
        <TestingList>
          <TestingListItem>
            <TestingLabel>
              <TestingLabelIcon bgColor="transparent" iconColor="#9333EA" large>
                <CircleTrainingIcon />
              </TestingLabelIcon>
              <span>Strength</span>
            </TestingLabel>
            <TestingValue>{trainingStrength.toLocaleString()}</TestingValue>
          </TestingListItem>
          <TestingListItem>
            <TestingLabel>
              <TestingLabelIcon bgColor="transparent" iconColor="#F97316" large>
                <CircleTrainingIcon />
              </TestingLabelIcon>
              <span>Hybrid</span>
            </TestingLabel>
            <TestingValue>{trainingHybrid.toLocaleString()}</TestingValue>
          </TestingListItem>
          <TestingListItem>
            <TestingLabel>
              <TestingLabelIcon bgColor="transparent" iconColor="#38424D" large>
                <CircleTrainingIcon />
              </TestingLabelIcon>
              <span>Off Day</span>
            </TestingLabel>
            <TestingValue>{trainingOffDay.toLocaleString()}</TestingValue>
          </TestingListItem>
        </TestingList>
      </DashboardCardPlaceholder>
    </>
  );
};
